import { Animal } from "components/Signup/store/state/types";
import { useWebTranslation } from "translations";
import { Typography } from "ui-kit/Typography";
import styles from "./AnimalSummary.module.css";
import { SpeciesEnum } from "../../../../../__generated__/globalTypes";
import { AnimalButtons } from "../AnimalButtons";
import { useAllValidAnimals } from "components/Signup/store/selectors/animals";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTitle,
} from "ui-kit";
import classNames from "classnames";
import { editAnimalById } from "components/Signup/store/slices/animals";
import { useSignupStore } from "components/Signup/store/hooks";
import { isMixedBreed } from "utils/insurance";
import { trackSignupEvent } from "components/Signup/analytics";
import { StepName } from "components/Signup/steps/types";
import { formatDate } from "utils/utils";
import { getDateFormat } from "utils/site";
import { useCustomer } from "components/Signup/store/selectors/customer";

const DetailRow = ({
  title,
  value,
}: {
  title: string;
  value?: string | null;
}) => {
  if (!value) {
    return null;
  }

  return (
    <div className={styles.row}>
      <Typography variant="body3" color="grey500">
        {title}
      </Typography>
      <Typography variant="body3">{value}</Typography>
    </div>
  );
};

const Avatar = ({ species }: { species: SpeciesEnum | null }) => {
  if (!species) return null;

  return (
    <div className={classNames(styles.avatar, styles[species])}>
      {species === SpeciesEnum.dog ? "🐶" : "🐱"}
    </div>
  );
};

export const AnimalSummary = ({
  animal,
  isOpen,
}: {
  animal: Animal;
  isOpen: boolean;
}) => {
  const { t } = useWebTranslation();
  const { dispatch, getState } = useSignupStore();
  const animals = useAllValidAnimals();
  const customer = useCustomer();

  return (
    <Accordion collapsible defaultIndex={isOpen ? 0 : undefined}>
      <AccordionItem className={styles.accordionItem}>
        <AccordionTitle className={styles.accordionTitle}>
          <div className={styles.titleContainer}>
            <Avatar species={animal.species} />
            <Typography variant="body2">
              <strong>{animal.name}</strong>
            </Typography>
          </div>
        </AccordionTitle>
        <AccordionPanel>
          <div className={styles.container}>
            <DetailRow
              title={t("global.sex")}
              value={t(`sex.dog_pronoun_${animal.sex}`)}
            />
            <DetailRow
              title={t("global.breed")}
              value={
                isMixedBreed(animal.breed?.id)
                  ? `${animal.mixedBreedFHalf?.name}, ${animal.mixedBreedSHalf?.name}`
                  : animal.breed?.name
              }
            />
            {animal.birthday ? (
              <DetailRow
                title={t("global.birthdate")}
                value={formatDate(animal.birthday, getDateFormat())}
              />
            ) : null}
            <DetailRow
              title={t(`global.neutered_${animal.sex}`)}
              value={t(
                animal.sexSituation === "castrated" ? "global.yes" : "global.no"
              )}
            />
            {animal.species === SpeciesEnum.cat ? (
              <DetailRow
                title={t("signup.summary.access.label")}
                value={t(`signup.summary.access.${animal.access}`)}
              />
            ) : null}
            <DetailRow
              title={t(`signup.summary.lives_in`)}
              value={customer.address?.city}
            />
            <AnimalButtons
              id={animal.id}
              showDeleteButton={animals.length > 1}
              onEdit={() => {
                trackSignupEvent("EDIT_ANIMAL" as StepName, 0, getState());
                dispatch(editAnimalById(animal.id));
              }}
            />
          </div>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
